/**
 * HeatmapCell Component
 * Individual cell in the risk heatmap grid with color-coded background
 */

import type { RiskLevel, BusinessCriticality } from "../types";

interface HeatmapCellProps {
  riskLevel: RiskLevel;
  businessCriticality: BusinessCriticality;
  count: number;
  onClick?: () => void;
}

/**
 * Color mapping based on risk level and business criticality combination
 * Colors gradient from green (low risk) to pink/red (high risk)
 */
const colorMap: Record<RiskLevel, Record<BusinessCriticality, string>> = {
  High: {
    Low: "bg-[#FEFCE8]", // yellow
    Medium: "bg-[#FEE7EF]", // light pink
    High: "bg-[#FDD0DF]", // pink
    Critical: "bg-[#FAA0BF]", // dark pink
  },
  Medium: {
    Low: "bg-[#D1F4E0]", // light green
    Medium: "bg-[#FEFCE8]", // yellow
    High: "bg-[#FEE7EF]", // light pink
    Critical: "bg-[#FDD0DF]", // pink
  },
  Low: {
    Low: "bg-[#E8FAF0]", // pale green
    Medium: "bg-[#D1F4E0]", // light green
    High: "bg-[#FEFCE8]", // yellow
    Critical: "bg-[#FEE7EF]", // light pink
  },
};

/**
 * Get background color class for a cell based on risk and criticality
 */
function getCellColor(
  riskLevel: RiskLevel,
  businessCriticality: BusinessCriticality
): string {
  return colorMap[riskLevel]?.[businessCriticality] ?? "bg-gray-100";
}

export const HeatmapCell = ({
  riskLevel,
  businessCriticality,
  count,
  onClick,
}: HeatmapCellProps) => {
  const bgColor = getCellColor(riskLevel, businessCriticality);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      className={`w-[68px] h-[68px] rounded-xl flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${bgColor}`}
    >
      <span className="font-inter font-medium text-lg text-[#0F172B]">
        {count}
      </span>
    </div>
  );
};

export default HeatmapCell;
